import React, { useState, useRef, useEffect } from "react";
import { normalizeCompany } from "../utils/companyUtils";
import { PREDEFINED_SUBTASKS } from "../utils/subtaskPresets";

export default function PovinnostiForm({
  formText,
  setFormText,
  setAutocompleteText,
  uniqueTaskTemplatesFiltered = [],
  showTaskSuggestions,
  setShowTaskSuggestions,
  povinnostInputRef,
  company,
  setCompany,
  deadline,
  setDeadline,
  deadlineTime,
  setDeadlineTime,
  repeatInterval,
  setRepeatInterval,
  estimatedTime,
  setEstimatedTime,
  subtasksInput,
  setSubtasksInput,
  formFiles,
  setFormFiles,
  companyList = [],
  addToCompanyList,
  taskList = [],
  addToTaskList,
  formPeriod,
  setFormPeriod,
  handleAddTasks
}) {
  // Spoločný štýl pre všetky inputy, aby boli v jednej rovine
  const commonInputStyle = {
    height: '36px',
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'white'
  };

  const [subtaskPickerOpen, setSubtaskPickerOpen] = useState(false);
  const [customSubtaskInput, setCustomSubtaskInput] = useState('');
  const subtaskPickerRef = useRef(null);

  const parsedLines = subtasksInput ? subtasksInput.split('\n').map(s => s.trim()).filter(Boolean) : [];
  const selectedPredefined = new Set(parsedLines.filter(l => PREDEFINED_SUBTASKS.includes(l)));
  const customItems = parsedLines.filter(l => !PREDEFINED_SUBTASKS.includes(l));

  const togglePredefined = (item) => {
    const newSelected = new Set(selectedPredefined);
    if (newSelected.has(item)) newSelected.delete(item);
    else newSelected.add(item);
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => newSelected.has(p)),
      ...customItems,
    ];
    setSubtasksInput(newLines.join('\n'));
  };

  const addCustomItem = (text) => {
    if (!text.trim()) return;
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => selectedPredefined.has(p)),
      ...customItems,
      text.trim(),
    ];
    setSubtasksInput(newLines.join('\n'));
    setCustomSubtaskInput('');
  };

  const removeCustomItem = (item) => {
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => selectedPredefined.has(p)),
      ...customItems.filter(c => c !== item),
    ];
    setSubtasksInput(newLines.join('\n'));
  };

  useEffect(() => {
    if (!subtaskPickerOpen) return;
    const handler = (e) => {
      if (subtaskPickerRef.current && !subtaskPickerRef.current.contains(e.target))
        setSubtaskPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [subtaskPickerOpen]);

  const handleCompanyBlur = () => {
    if (company) setCompany(normalizeCompany(company));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const normalized = normalizeCompany(company);
    if (normalized && !companyList.includes(normalized)) {
      const ok = window.confirm(`Firma "${normalized}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToCompanyList) addToCompanyList(normalized);
    }
    if (normalized) setCompany(normalized);

    const taskNames = formText.split(',').map(t => t.trim()).filter(Boolean);
    for (const name of taskNames) {
      if (name && !taskList.includes(name)) {
        const ok = window.confirm(`Povinnosť "${name}" nie je v zozname. Pridať ju?`);
        if (!ok) return;
        if (addToTaskList) addToTaskList(name);
      }
    }

    handleAddTasks(e);
  };

  return (
    <form onSubmit={handleSubmit} style={{ 
      maxWidth: "1350px", 
      margin: "0.5rem auto", 
      background: "rgba(255,255,255,0.1)", 
      borderRadius: 10, 
      padding: '10px 15px', 
      boxShadow: "0 2px 16px rgba(0,0,0,0.1)" 
    }}>
      <div style={{ 
        display: "flex", 
        gap: 10, 
        alignItems: "center", // Toto ich zarovná do jednej výškovej línie
        flexWrap: "nowrap" // Držíme to v jednom riadku
      }}>
        
        {/* Názov povinnosti */}
        <div style={{ position: 'relative', flex: 3, minWidth: 200 }}>
          <input
            type="text"
            list="task-list-form"
            value={formText}
            onChange={e => {
              setFormText(e.target.value);
              setAutocompleteText(e.target.value);
            }}
            placeholder="Povinnosť..."
            style={{ ...commonInputStyle, width: '100%' }}
            required
            ref={povinnostInputRef}
            autoComplete="off"
            onFocus={() => setShowTaskSuggestions(true)}
            onBlur={() => setTimeout(() => setShowTaskSuggestions(false), 150)}
          />
          <datalist id="task-list-form">
            {taskList.map(t => <option key={t} value={t} />)}
          </datalist>
          {showTaskSuggestions && (
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: 6,
                zIndex: 100,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 14,
                maxHeight: 250,
                overflowY: 'auto',
                marginTop: '5px'
            }}>
              <div
                onClick={() => {
                  setFormText('mesačné účtovníctvo');
                  setAutocompleteText('mesačné účtovníctvo');
                  setSubtasksInput('VFA (vyšlé faktúry)\nDFA (došlé faktúry)\nBanka – príjmy aj výdaje\nPokladňa – cash príjmy\nPokladňa – cash výdaje\nPokladňa – karta výdaje\nPohľadávky\nZáväzky\nID (interné doklady)\nPodanie DPH a KV k DPH\nPodanie DP k DPH\nVystavovanie faktúr');
                  setShowTaskSuggestions(false);
                }}
                style={{ padding: '8px 12px', cursor: 'pointer', color: '#166534', fontWeight: 'bold', background: '#f0fdf4' }}
              >
                ➕ mesačné účtovníctvo (šablóna)
              </div>
              <div
                onClick={() => {
                  setFormText('prihlasenie zamestnanca');
                  setAutocompleteText('prihlasenie zamestnanca');
                  setSubtasksInput('dotazník vyplnený\nprihlásenie SP\nprihlásenie ZP\npracovná zmluva\nvyhlásenie k dani\nvyhlásenie OOP socpoist');
                  setShowTaskSuggestions(false);
                }}
                style={{ padding: '8px 12px', cursor: 'pointer', color: '#1976D2', fontWeight: 'bold', borderTop: '1px solid #f0f0f0' }}
              >
                ➕ prihlasenie zamestnanca (šablóna)
              </div>
              {uniqueTaskTemplatesFiltered.map((tpl, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setFormText(tpl.text);
                    setAutocompleteText(tpl.text);
                    setSubtasksInput(tpl.subtasks || '');
                    setShowTaskSuggestions(false);
                  }}
                  style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', cursor: 'pointer' }}
                >
                  {tpl.text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Firma */}
        <input
          type="text"
          list="company-list-form"
          value={company}
          onChange={e => setCompany(e.target.value.toUpperCase())}
          onBlur={handleCompanyBlur}
          placeholder="Firma..."
          style={{ ...commonInputStyle, flex: "1.5", minWidth: 130 }}
        />
        <datalist id="company-list-form">
          {companyList.map(c => <option key={c} value={c} />)}
        </datalist>

        {/* Termín */}
        <input
          type="date"
          value={deadline}
          onChange={e => setDeadline(e.target.value)}
          style={{ ...commonInputStyle, width: "135px" }}
          required
        />

        {/* Obdobie */}
        <input
          type="month"
          value={formPeriod || ''}
          onChange={e => setFormPeriod && setFormPeriod(e.target.value)}
          title="Účtovné obdobie"
          style={{ ...commonInputStyle, width: "140px" }}
          required
        />

        {/* Čas termínu (voliteľný) */}
        <input
          type="time"
          value={deadlineTime || ""}
          onChange={e => setDeadlineTime && setDeadlineTime(e.target.value)}
          title="Čas termínu (voliteľné)"
          style={{ ...commonInputStyle, width: "100px" }}
        />

        {/* Interval */}
        <select
          value={repeatInterval}
          onChange={e => setRepeatInterval(e.target.value)}
          style={{ ...commonInputStyle, width: "110px" }}
        >
          <option value="none">Bez opakovania</option>
          <option value="weekly">Týždenne</option>
          <option value="monthly">Mesačne</option>
          <option value="quarterly">Štvrťročne</option>
          <option value="annually">Ročne</option>
        </select>

        {/* Čas */}
        <input
          type="number"
          value={estimatedTime}
          onChange={e => setEstimatedTime(e.target.value)}
          placeholder="Min"
          style={{ ...commonInputStyle, width: "65px" }}
          required
        />

        {/* Podúlohy - picker s checkboxmi */}
        <div style={{ position: 'relative', flex: '1.5', minWidth: 130 }} ref={subtaskPickerRef}>
          <button
            type="button"
            onClick={() => setSubtaskPickerOpen(v => !v)}
            style={{ ...commonInputStyle, width: '100%', textAlign: 'left', cursor: 'pointer', background: parsedLines.length > 0 ? '#f0fdf4' : 'white', border: parsedLines.length > 0 ? '1px solid #86efac' : '1px solid #ddd' }}
          >
            {parsedLines.length > 0 ? `${parsedLines.length} podúloh ▾` : 'Podúlohy... ▾'}
          </button>
          {subtaskPickerOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, minWidth: 280, background: 'white', border: '1px solid #ccc', borderRadius: 6, zIndex: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '6px 0' }}>
              {PREDEFINED_SUBTASKS.map(item => (
                <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer', fontSize: 13, background: selectedPredefined.has(item) ? '#f0fdf4' : 'transparent' }}>
                  <input type="checkbox" checked={selectedPredefined.has(item)} onChange={() => togglePredefined(item)} />
                  {item}
                </label>
              ))}
              {customItems.length > 0 && (
                <div style={{ borderTop: '1px solid #eee', marginTop: 4, paddingTop: 4 }}>
                  {customItems.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 12px', fontSize: 13 }}>
                      <span style={{ flex: 1, color: '#334155' }}>· {item}</span>
                      <button type="button" onClick={() => removeCustomItem(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ borderTop: '1px solid #eee', padding: '6px 12px', display: 'flex', gap: 4, marginTop: 4 }}>
                <input
                  type="text"
                  value={customSubtaskInput}
                  onChange={e => setCustomSubtaskInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(customSubtaskInput); } }}
                  placeholder="Pridať vlastnú..."
                  style={{ flex: 1, fontSize: 12, padding: '3px 6px', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <button type="button" onClick={() => addCustomItem(customSubtaskInput)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 14 }}>+</button>
              </div>
            </div>
          )}
        </div>

        {/* Príloha */}
        <label style={{ 
          cursor: 'pointer', 
          padding: '0 12px', 
          background: '#eceff1', 
          color: '#455a64', 
          borderRadius: '6px', 
          fontSize: '13px', 
          height: '36px', 
          display: 'flex', 
          alignItems: 'center',
          border: '1px solid #cfd8dc',
          whiteSpace: 'nowrap'
        }}>
          📎 {formFiles.length > 0 ? `${formFiles.length} súb.` : 'Súbory'}
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setFormFiles(prev => [...prev, ...files]);
              e.target.value = '';
            }}
          />
        </label>

        {/* Tlačidlo PRIDAŤ */}
        <button type="submit" style={{ 
          padding: "0 20px", 
          borderRadius: 6, 
          fontSize: 14, 
          fontWeight: 'bold',
          height: '36px', 
          background: '#2e7d32', 
          color: 'white', 
          border: 'none', 
          cursor: 'pointer',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          PRIDAŤ
        </button>
      </div>
    </form>
  );
}